// Leave-day arithmetic for the one-study-day-a-week rule.
//
// The rule: you get one study day per week, and you forfeit it in any week you
// take annual leave. So a leave day only buys you a weekday off if it is the
// second or later leave day in that same week.
//
//   0 leave in a week -> 1 weekday off (the study day)
//   1 leave in a week -> 1 weekday off  (bought nothing)
//   3 leave in one week -> 3 weekdays off (net +2)
//   3 leave across a weekend -> two weeks touched, two study days lost (net +1)
//
// Net gain = leaveDays - (number of distinct weeks containing leave).
// Pure functions only; no React, no Supabase.

const DAY_MS = 24 * 60 * 60 * 1000

export function parseLeaveDates(value) {
  const raw = Array.isArray(value)
    ? value
    : String(value || '').split(/[\s,;]+/)
  const seen = new Set()
  const dates = []
  raw.forEach((entry) => {
    const text = String(entry || '').trim()
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return
    const date = new Date(`${text}T12:00:00Z`)
    if (Number.isNaN(date.getTime())) return
    if (seen.has(text)) return
    seen.add(text)
    dates.push(text)
  })
  return dates.sort()
}

// ISO week key (Monday-start), e.g. "2026-W31". Weeks are what the study-day
// rule keys on, so Monday-start matters: a Friday and the following Monday are
// two different weeks and cost two study days.
export function isoWeekKey(isoDate) {
  const date = new Date(`${isoDate}T12:00:00Z`)
  if (Number.isNaN(date.getTime())) return null
  const target = new Date(date.getTime())
  const dayNumber = (date.getUTCDay() + 6) % 7 // Monday = 0
  target.setUTCDate(target.getUTCDate() - dayNumber + 3) // Thursday of this week
  const isoYear = target.getUTCFullYear()
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4))
  const firstDayNumber = (firstThursday.getUTCDay() + 6) % 7
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNumber + 3)
  const week = 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * DAY_MS))
  return `${isoYear}-W${String(week).padStart(2, '0')}`
}

export function isWeekend(isoDate) {
  const day = new Date(`${isoDate}T12:00:00Z`).getUTCDay()
  return day === 0 || day === 6
}

/**
 * @param {string[]|string} value leave dates as ISO strings (array or free text)
 * @returns {{
 *   dates: string[], leaveDays: number, weeks: string[], weekCount: number,
 *   netGain: number, weekendDates: string[], perWeek: {week: string, days: string[]}[],
 *   wasted: boolean, warning: string
 * }}
 */
export function leaveCost(value) {
  const dates = parseLeaveDates(value)
  const perWeekMap = new Map()
  dates.forEach((date) => {
    const key = isoWeekKey(date)
    if (!key) return
    if (!perWeekMap.has(key)) perWeekMap.set(key, [])
    perWeekMap.get(key).push(date)
  })

  const perWeek = [...perWeekMap.entries()]
    .map(([week, days]) => ({ week, days }))
    .sort((a, b) => a.week.localeCompare(b.week))

  const leaveDays = dates.length
  const weekCount = perWeek.length
  const netGain = leaveDays - weekCount
  const weekendDates = dates.filter(isWeekend)
  const singletonWeeks = perWeek.filter((entry) => entry.days.length === 1)

  let warning = ''
  if (leaveDays > 0 && netGain <= 0) {
    warning = weekCount === 1
      ? 'One leave day in a week only replaces the study day you lose — it buys no extra time off.'
      : `${leaveDays} leave days spread over ${weekCount} weeks forfeits ${weekCount} study days, so you gain nothing.`
  } else if (singletonWeeks.length) {
    warning = `${singletonWeeks.length} week${singletonWeeks.length === 1 ? '' : 's'} with a single leave day — that day buys nothing. Clustering it would.`
  }
  if (weekendDates.length) {
    warning = [warning, `${weekendDates.length} of these dates fall on a weekend and probably should not be booked as leave.`]
      .filter(Boolean).join(' ')
  }

  return {
    dates,
    leaveDays,
    weeks: perWeek.map((entry) => entry.week),
    weekCount,
    netGain,
    weekendDates,
    perWeek,
    wasted: leaveDays > 0 && netGain <= 0,
    warning,
  }
}

export function describeLeaveCost(result) {
  if (!result.leaveDays) return 'No leave dates set.'
  const weekdaysOff = result.leaveDays
  return `${result.leaveDays} leave day${result.leaveDays === 1 ? '' : 's'} across ${result.weekCount} week${result.weekCount === 1 ? '' : 's'} → ${weekdaysOff} weekday${weekdaysOff === 1 ? '' : 's'} off, net ${result.netGain >= 0 ? '+' : ''}${result.netGain} versus taking none.`
}
